// src/app/services/property.service.ts

import { Injectable } from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {catchError, Observable, of} from 'rxjs';
import {AuthService} from './auth.service';
import {
  EmergencyInstruction,
  HospitalityLinkDocument,
  Property,
  PropertyDocumentsResponse,
  PropertyDocumentSection
} from '../property-list/property-list.models';
import {BE_API_AUTH_URL, BE_API_URL} from '../constants/app.constants';
import {
  QrPosterIconAssets,
  QrPosterRenderDescriptorResponse,
} from '../models/qr-poster-render-descriptor.model';

export type PropertyBulkApplyScope = 'current' | 'selected' | 'all';

export interface BulkPropertyMutationSummary {
  matched: number;
  updated: number;
  deleted: number;
  notFound: number;
  skipped: number;
  errors: number;
  resetEmergencyInstructionActions?: number;
}

export interface BulkPropertyMutationResult {
  propertyId?: string;
  address?: string;
  status: 'updated' | 'deleted' | 'not_found' | 'skipped' | 'error';
  reason?: string;
  matchedBy?: string;
  resetEmergencyInstructionActions?: number;
}

export interface PropertyBulkApplyTargetsPayload {
  scope: PropertyBulkApplyScope;
  targetPropertyIds?: string[];
}

export interface BulkDocumentSectionMatchPayload {
  sectionId?: string;
  sourceSectionId?: string;
  originSectionId?: string;
  originPropertyId?: string;
  bulkGroupId?: string;
  fingerprint?: string;
  fileFingerprint?: string;
  normalizedTitle?: string;
}

export interface BulkEmergencyInstructionMatchPayload {
  instructionId?: string;
  sourceInstructionId?: string;
  originInstructionId?: string;
  originPropertyId?: string;
  bulkGroupId?: string;
  fingerprint?: string;
  category?: string;
  scenarioKey?: string;
  title?: string;
}

export interface BulkApplyDocumentSectionPayload extends PropertyBulkApplyTargetsPayload {
  sourceItemId?: string;
  match?: BulkDocumentSectionMatchPayload;
  section: PropertyDocumentSection;
}

export interface BulkApplyDocumentSectionResponse {
  appliedPropertyCount: number;
  createdSections: number;
  mergedSections: number;
  addedFiles: number;
  duplicateFilesSkipped: number;
  targetPropertyIds: string[];
}

export interface BulkApplyEmergencyInstructionPayload extends PropertyBulkApplyTargetsPayload {
  sourceItemId?: string;
  match?: BulkEmergencyInstructionMatchPayload;
  instruction: EmergencyInstruction;
}

export interface BulkApplyEmergencyInstructionResponse {
  success?: boolean;
  appliedPropertyCount: number;
  createdInstructions: number;
  targetPropertyIds: string[];
  summary?: BulkPropertyMutationSummary;
  results?: BulkPropertyMutationResult[];
}

export interface BulkEditDocumentSectionPayload extends PropertyBulkApplyTargetsPayload {
  sourceItemId?: string;
  match: BulkDocumentSectionMatchPayload;
  payload: {
    section: PropertyDocumentSection;
  };
}

export interface BulkDeleteDocumentSectionPayload extends PropertyBulkApplyTargetsPayload {
  sourceItemId?: string;
  match: BulkDocumentSectionMatchPayload;
}

export interface BulkEditEmergencyInstructionPayload extends PropertyBulkApplyTargetsPayload {
  sourceItemId?: string;
  match: BulkEmergencyInstructionMatchPayload;
  payload: {
    instruction: EmergencyInstruction;
  };
}

export interface BulkDeleteEmergencyInstructionPayload extends PropertyBulkApplyTargetsPayload {
  sourceItemId?: string;
  match: BulkEmergencyInstructionMatchPayload;
}

export interface BulkPropertyMutationResponse {
  success: boolean;
  summary: BulkPropertyMutationSummary;
  results: BulkPropertyMutationResult[];
}

export type DistributionStatusEntityType = 'emergency_instruction' | 'document_section';
export type DistributionStatusValue = 'source' | 'copied' | 'missing' | 'modified' | 'legacy_match' | 'conflict';

export interface DistributionStatusProperty {
  propertyId: string;
  address?: string;
  status: DistributionStatusValue;
  matchedItemId?: string;
  matchedBy?: string;
  updatedAt?: string;
  reason?: string;
  debug?: Record<string, unknown>;
}

export interface DistributionStatusResponse {
  entityType: DistributionStatusEntityType;
  sourcePropertyId: string | null;
  sourceItemId: string | null;
  sourcePropertyAddress?: string | null;
  sourceItemDeleted?: boolean;
  sourcePropertyDeleted?: boolean;
  sourceTitle?: string;
  properties: DistributionStatusProperty[];
  summary: {
    source: number;
    copied: number;
    missing: number;
    modified: number;
    legacyMatch: number;
    conflict: number;
  } | null;
  debug?: Record<string, unknown>;
}

export interface DistributionOverviewGroupProperty {
  propertyId: string;
  address?: string;
  status: DistributionStatusValue;
  matchedItemId?: string;
  matchedBy?: string;
  updatedAt?: string;
  reason?: string;
}

export interface DistributionOverviewGroup {
  rootItemId: string;
  rootItemTitle?: string;
  sourcePropertyId?: string;
  sourcePropertyAddress?: string;
  sourceFound: boolean;
  sourceDeleted: boolean;
  entityCount: number;
  propertyCount: number;
  fileCount?: number;
  summary: {
    source: number;
    copied: number;
    missing: number;
    modified: number;
    legacyMatch: number;
    conflict: number;
  };
  properties?: DistributionOverviewGroupProperty[];
  unlinked?: boolean;
}

export interface DistributionOverviewResponse {
  entityType: DistributionStatusEntityType;
  groups: DistributionOverviewGroup[];
  unlinkedCount: number;
}

export interface DistributionOverviewGroupDetailsResponse {
  entityType: DistributionStatusEntityType;
  group: DistributionOverviewGroup;
}

export interface PromoteDistributionSourcePayload {
  entityType: DistributionStatusEntityType;
  itemId: string;
}

export interface PromoteDistributionSourceResponse {
  promoted: boolean;
  entityType: DistributionStatusEntityType;
  promotedPropertyId: string;
  promotedItemId: string;
  previousRootPropertyId: string | null;
  previousRootItemId: string;
  updatedCopyCount: number;
}

export interface PropertyRatingDetailApi {
  cleanliness: number;
  comfort: number;
  communication: number;
  overall: number;
  comment: string | null;
  submittedAt: string | null;
}

export interface PropertyRatingApi {
  propertyId: string;
  propertyAddress: string;
  summary?: {
    count: number;
    averages?: {
      cleanliness: number;
      comfort: number;
      communication: number;
      overall: number;
    };
    overallAverage?: number;
    lastSubmittedAt?: string | null; // ISO string
  };
  ratings: PropertyRatingDetailApi[];
}

export interface HospitalityChatResponse {
  answer: string;
  sources?: Array<{ title?: string; url?: string; [key: string]: any }>;
  turnId: string;
  language: string;
}

export interface HospitalityChatVoteResponse {
  turnId: string;
  vote: 'up' | 'down';
  counts: { up: number; down: number };
  helpfulRate?: number | null;
}

export interface HospitalityChatSignedAssetResponse {
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class PropertyService {

  constructor(private http: HttpClient, private authService: AuthService) {}

  // Get all properties
  getProperties(): Observable<Property[]> {
    return this.http
      .get<Property[]>(`${BE_API_AUTH_URL}/list`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getRatings(): Observable<PropertyRatingApi[]> {
    return this.http
      .get<PropertyRatingApi[]>(`${BE_API_AUTH_URL}/ratings`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  searchPropertyOptions(
    search: string,
    page: number,
    pageSize: number,
    idsOnly = false,
  ): Observable<{ items: { id: string; label: string }[]; page: number; pageSize: number; total: number; hasMore: boolean }> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    if (search) {
      params = params.set('search', search);
    }
    if (idsOnly) {
      params = params.set('idsOnly', 'true');
    }

    return this.http
      .get<{ items: { id: string; label: string }[]; page: number; pageSize: number; total: number; hasMore: boolean }>(
        `${BE_API_AUTH_URL}/properties/options`,
        { headers: this.authService.createAuthorizationHeader(), params }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  // Create a new property
  createProperty(property: Property): Observable<Property> {
    return this.http
      .post<Property>(`${BE_API_AUTH_URL}/create`, property, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  // Update an existing property
  updateProperty(propertyId: string, property: Property): Observable<Property> {
    return this.http
      .put<Property>(`${BE_API_AUTH_URL}/properties/${propertyId}`, property, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  deleteProperty(propertyId: string): Observable<any> {
    return this.http
      .delete(`${BE_API_AUTH_URL}/properties/${propertyId}`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  // Import properties from JSON data instead of a file
  importProperties(data: any): Observable<any> {
    return this.http
      .post(`${BE_API_AUTH_URL}/import`, { properties: data }, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  // Fetch employees and services for the company
  fetchCompanyEmployeesAndServices(): Observable<any> {
    return this.http
      .get(`${BE_API_AUTH_URL}/employees-and-services`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getPropertyDetails(companyId: string, propertyId: string, visitorId?: string | null): Observable<any> {
    const url = `${BE_API_URL}/properties/${companyId}/${propertyId}`;
    const headers = visitorId
      ? new HttpHeaders({ 'X-Scanafy-Visitor-Id': visitorId })
      : undefined;
    return this.http.get<any>(url, { headers });
  }

  getPropertyLandingPreview(propertyId: string): Observable<any> {
    return this.http
      .get<any>(`${BE_API_AUTH_URL}/properties/${propertyId}/landing-preview`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getQrPosterRenderDescriptor(
    propertyId: string,
    locale: 'de' | 'en',
    forceDefault = false,
    templateId?: string | null,
  ): Observable<QrPosterRenderDescriptorResponse> {
    let params = new HttpParams().set('locale', locale);
    if (forceDefault) {
      params = params.set('templateSource', 'default');
    }
    if (templateId) {
      params = params.set('templateId', templateId);
    }

    return this.http
      .get<QrPosterRenderDescriptorResponse>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/qr-poster-render-descriptor`,
        {
          headers: this.authService.createAuthorizationHeader(),
          params,
        }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getQrPosterEditorPreviewDescriptor(
    locale: 'de' | 'en',
  ): Observable<QrPosterRenderDescriptorResponse> {
    return this.http
      .get<QrPosterRenderDescriptorResponse>(
        `${BE_API_AUTH_URL}/properties/qr-poster-editor-preview-descriptor`,
        {
          headers: this.authService.createAuthorizationHeader(),
          params: new HttpParams().set('locale', locale),
        }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getQrPosterLogoBlob(propertyId: string): Observable<Blob | null> {
    return this.http
      .get(`${BE_API_AUTH_URL}/properties/${propertyId}/qr-poster-logo`, {
        headers: this.authService.createAuthorizationHeader(),
        responseType: 'blob',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(null);
          }
          return this.authService.handleError(error);
        })
      );
  }

  submitRating(
    companyId: string,
    propertyId: string,
    payload: { cleanliness: number; comfort: number; communication: number; overall: number; comment?: string }
  ): Observable<any> {
    return this.http.post(`${BE_API_URL}/properties/${companyId}/${propertyId}/ratings`, payload);
  }

  // Get colored QR code Icons avoiding processing the SVG to PNG in FE
  getColoredIcons(
    themeColor?: string,
    iconNames?: readonly string[],
  ): Observable<QrPosterIconAssets> {
    const payload: { themeColor?: string; iconNames?: string[] } = {};
    if (themeColor) {
      payload.themeColor = themeColor;
    }
    if (iconNames !== undefined) {
      payload.iconNames = [...iconNames];
    }

    return this.http.post<QrPosterIconAssets>(
      `${BE_API_AUTH_URL}/icons/colorize`,
      payload,
      { headers: this.authService.createAuthorizationHeader() }
    );
  }

  uploadFile(propertyId: string, file: File, sectionIndex: number): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sectionIndex', sectionIndex.toString());

    return this.http.post(
      `${BE_API_AUTH_URL}/properties/${propertyId}/documents`,
      formData,
      {
        headers: this.authService.createAuthorizationHeader(),
      }
    ).pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  saveDocuments(propertyId: string, sections: PropertyDocumentSection[]): Observable<any> {
    return this.http.post(
      `${BE_API_AUTH_URL}/properties/${propertyId}/documents/save`,
      { sections },
      {
        headers: this.authService.createAuthorizationHeader()
      }
    ).pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getPropertyDocumentsAuth(propertyId: string): Observable<PropertyDocumentsResponse | PropertyDocumentSection[]> {
    return this.http
      .get<PropertyDocumentsResponse | PropertyDocumentSection[]>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/documents`,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getPropertyDocumentsPublic(propertyId: string): Observable<PropertyDocumentSection[]> {
    return this.http
      .get<PropertyDocumentSection[]>(`${BE_API_URL}/properties/${propertyId}/documents`)
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getEmergencyInstructions(propertyId: string, companyId?: string) {
    const url =
      companyId
        ? `${BE_API_URL}/properties/${propertyId}/emergency-instructions?companyId=${encodeURIComponent(companyId)}`
        : `${BE_API_URL}/properties/${propertyId}/emergency-instructions`;

    return this.http.get<any[]>(url);
  }

  createEmergencyInstruction(
    propertyId: string,
    payload: EmergencyInstruction
  ): Observable<EmergencyInstruction[]> {
    return this.http
      .post<EmergencyInstruction[]>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/emergency-instructions`,
        payload,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  updateEmergencyInstruction(
    propertyId: string,
    instructionId: string,
    payload: EmergencyInstruction
  ): Observable<EmergencyInstruction[]> {
    return this.http
      .put<EmergencyInstruction[]>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/emergency-instructions/${instructionId}`,
        payload,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  deleteEmergencyInstruction(propertyId: string, instructionId: string): Observable<EmergencyInstruction[]> {
    return this.http
      .delete<EmergencyInstruction[]>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/emergency-instructions/${instructionId}`,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  deleteFile(propertyId: string, s3Key: string): Observable<any> {
    return this.http.delete(
      `${BE_API_AUTH_URL}/properties/${propertyId}/documents/file?s3Key=${encodeURIComponent(s3Key)}`,
      {
        headers: this.authService.createAuthorizationHeader()
      }
    ).pipe(
      catchError(this.authService.handleError.bind(this.authService))
    );
  }

  /** Delete one entire document section (and all its files) */
  deleteSection(propertyId: string, sectionIndex: number): Observable<any> {
    return this.http
      .delete(
        `${BE_API_AUTH_URL}/properties/${propertyId}/documents/section/${sectionIndex}`,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  bulkApplyDocumentSection(
    propertyId: string,
    payload: BulkApplyDocumentSectionPayload
  ): Observable<BulkApplyDocumentSectionResponse> {
    return this.http
      .post<BulkApplyDocumentSectionResponse>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/documents/section/bulk-apply`,
        payload,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  bulkApplyEmergencyInstruction(
    propertyId: string,
    payload: BulkApplyEmergencyInstructionPayload
  ): Observable<BulkApplyEmergencyInstructionResponse> {
    return this.http
      .post<BulkApplyEmergencyInstructionResponse>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/emergency-instructions/bulk-apply`,
        payload,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  bulkEditDocumentSection(
    propertyId: string,
    payload: BulkEditDocumentSectionPayload
  ): Observable<BulkPropertyMutationResponse> {
    return this.http
      .post<BulkPropertyMutationResponse>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/documents/section/bulk-edit`,
        payload,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  bulkDeleteDocumentSection(
    propertyId: string,
    payload: BulkDeleteDocumentSectionPayload
  ): Observable<BulkPropertyMutationResponse> {
    return this.http
      .post<BulkPropertyMutationResponse>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/documents/section/bulk-delete`,
        payload,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  bulkEditEmergencyInstruction(
    propertyId: string,
    payload: BulkEditEmergencyInstructionPayload
  ): Observable<BulkPropertyMutationResponse> {
    return this.http
      .post<BulkPropertyMutationResponse>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/emergency-instructions/bulk-edit`,
        payload,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  bulkDeleteEmergencyInstruction(
    propertyId: string,
    payload: BulkDeleteEmergencyInstructionPayload
  ): Observable<BulkPropertyMutationResponse> {
    return this.http
      .post<BulkPropertyMutationResponse>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/emergency-instructions/bulk-delete`,
        payload,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getDistributionStatus(
    propertyId: string,
    params: {
      entityType: DistributionStatusEntityType;
      entityId: string;
      bulkGroupId?: string;
      debug?: boolean;
    }
  ): Observable<DistributionStatusResponse> {
    let httpParams = new HttpParams()
      .set('entityType', params.entityType)
      .set('entityId', params.entityId);

    if (params.bulkGroupId) {
      httpParams = httpParams.set('bulkGroupId', params.bulkGroupId);
    }
    if (params.debug) {
      httpParams = httpParams.set('debug', 'true');
    }

    return this.http
      .get<DistributionStatusResponse>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/distribution-status`,
        {
          headers: this.authService.createAuthorizationHeader(),
          params: httpParams,
        }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getDistributionOverview(
    entityType: DistributionStatusEntityType
  ): Observable<DistributionOverviewResponse> {
    const params = new HttpParams().set('entityType', entityType);
    return this.http
      .get<DistributionOverviewResponse>(
        `${BE_API_AUTH_URL}/distribution-overview`,
        {
          headers: this.authService.createAuthorizationHeader(),
          params,
        }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getDistributionOverviewDetails(
    entityType: DistributionStatusEntityType,
    rootItemId: string,
    sourcePropertyId?: string
  ): Observable<DistributionOverviewGroupDetailsResponse> {
    let params = new HttpParams().set('entityType', entityType);
    if (sourcePropertyId) {
      params = params.set('sourcePropertyId', sourcePropertyId);
    }
    return this.http
      .get<DistributionOverviewGroupDetailsResponse>(
        `${BE_API_AUTH_URL}/distribution-overview/${rootItemId}/details`,
        {
          headers: this.authService.createAuthorizationHeader(),
          params,
        }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  promoteDistributionSource(
    propertyId: string,
    payload: PromoteDistributionSourcePayload
  ): Observable<PromoteDistributionSourceResponse> {
    return this.http
      .post<PromoteDistributionSourceResponse>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/distribution/promote-source`,
        payload,
        {
          headers: this.authService.createAuthorizationHeader(),
        }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  reorderEmergencyInstructions(propertyId: string, instructionIds: string[]): Observable<EmergencyInstruction[]> {
    return this.http
      .post<EmergencyInstruction[]>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/emergency-instructions/reorder`,
        { instructionIds },
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getCompanyScanStats(): Observable<any[]> {
    return this.http
      .get<any[]>(`${BE_API_AUTH_URL}/scan-stats-company`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getPushSubscriberProperties(params: {
    page: number;
    pageSize: number;
    search?: string;
    sortField?: 'address' | 'pushSubscribers';
    sortOrder?: 'ascend' | 'descend';
  }): Observable<{ items: Array<{ propertyId: string; address: string; pushSubscribers: number }>; total: number; page: number; pageSize: number }> {
    let httpParams = new HttpParams()
      .set('page', String(params.page))
      .set('pageSize', String(params.pageSize));

    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params.sortField) {
      httpParams = httpParams.set('sortField', params.sortField);
    }
    if (params.sortOrder) {
      httpParams = httpParams.set('sortOrder', params.sortOrder);
    }

    return this.http
      .get<{ items: Array<{ propertyId: string; address: string; pushSubscribers: number }>; total: number; page: number; pageSize: number }>(
        `${BE_API_AUTH_URL}/push-subscriber-properties`,
        { headers: this.authService.createAuthorizationHeader(), params: httpParams }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  sendHospitalityChatMessage(
    companyId: string,
    propertyId: string,
    question: string,
    language: string
  ): Observable<HospitalityChatResponse> {
    return this.http.post<HospitalityChatResponse>(
      `${BE_API_URL}/properties/${companyId}/${propertyId}/hospitality-chat`,
      { question, language }
    );
  }

  voteHospitalityChatTurn(
    companyId: string,
    propertyId: string,
    turnId: string,
    vote: 'up' | 'down',
    reason?: string | null,
  ): Observable<HospitalityChatVoteResponse> {
    const payload: { vote: 'up' | 'down'; reason?: string } = { vote };
    const trimmedReason = reason?.trim();
    if (trimmedReason) {
      payload.reason = trimmedReason;
    }
    return this.http.post<HospitalityChatVoteResponse>(
      `${BE_API_URL}/properties/${companyId}/${propertyId}/hospitality-chat/${turnId}/vote`,
      payload,
    );
  }

  signHospitalityChatAsset(
    companyId: string,
    propertyId: string,
    url: string,
  ): Observable<HospitalityChatSignedAssetResponse> {
    return this.http.post<HospitalityChatSignedAssetResponse>(
      `${BE_API_URL}/properties/${companyId}/${propertyId}/hospitality-chat/sign-asset`,
      { url },
    );
  }

  trackInteraction(
    companyId: string,
    propertyId: string,
    payload: {
      type:
        | 'document-view'
        | 'section-view'
        | 'language-change'
        | 'emergency-call'
        | 'emergency-instruction-view'
        | 'contact-click'
        | 'announcement-view'
        | 'announcement-attachment-view'
        | 'link-click'
        | 'push-subscribed'
        | 'push-unsubscribed'
        | 'hospitality-feedback'
        | 'copy';
    metadata?: any;
  }): Observable<any> {
    const body = { companyId, propertyId, ...payload };
    return this.http.post(`${BE_API_URL}/properties/track`, body);
  }

  uploadHospitalityLinkDocument(propertyId: string, file: File): Observable<HospitalityLinkDocument> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<HospitalityLinkDocument>(
        `${BE_API_AUTH_URL}/properties/${propertyId}/hospitality-links/document`,
        formData,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }
}
